import React, {useImperativeHandle, useState, forwardRef} from "react";
import {useNavigate} from "react-router-dom"
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {object, TypeOf, z} from "zod";
import {useSnackbar} from "notistack";
import {Grid, Stack, Drawer} from "@mui/material";
import Dialog from "@/components/dialog";

import {zodResolver} from "@hookform/resolvers/zod";
import EmployeeService from "@/services/hr/EmployeeService";
import DeviceService from "@/services/dms/DeviceService";
import {CancelButton, SavingButton} from "@/components/data-grid/CustomButton";
import EmployeeForm from "./EmployeeForm";
import GenericService from "@/services/GenericService";
import SnackbarUtil from "@/utils/SnackbarUtil";

const EmployeeNew = (props: any, ref: any) => {
  const {t} = useTranslation();
  const codeRegx = /^[a-zA-Z0-9]+$/
  const employeeValidate = object({
    code: z.string({required_error: t("common.This field is required!")}).min(1, t("common.This field is required!")).regex(codeRegx, t("common.No special character allowed!"))
      .max(14, t("common.The length of field cannot exceed digits", {number: 14})),
    firstName: z.string({required_error: t("common.This field is required!")}).min(1, t("common.This field is required!"))
      .max(25, t("common.The length of field cannot exceed digits", {number: 25})),
    lastName: z.string({required_error: t("common.This field is required!")}).min(1, t("common.This field is required!"))
      .max(25, t("common.The length of field cannot exceed digits", {number: 25})),
    departmentIdOrCode: z.string().optional(),
    email: z.string().max(50, t("common.The length of field cannot exceed digits", {number: 50})).optional(),
    verify: z.boolean().optional(),
    dept: z.object({
      id: z.string(),
      name: z.string().min(1, t("common.This field is required!")),
    }, {required_error: t("common.This field is required!"), invalid_type_error: t("common.This field is required!")}),
  })
  type employeeInput = TypeOf<typeof employeeValidate>;

  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const [saving, setSaving] = React.useState<boolean>(false)
  const title = t("common.New Person")
  const employeeForm = useForm<employeeInput>({
    resolver: zodResolver(employeeValidate),
  });

  const [open, setOpen] = useState(false)
  const onClose = () => {
    setOpen(false)
  }
  useImperativeHandle(ref, () => ({
    showAdd: async () => {
      employeeForm.reset({
        code: "",
        firstName: "",
        lastName: "",
        email: "",
        departmentIdOrCode: "",
        verify: false,
        dept: {id: "", name: ""}
      })
      setOpen(true)
    }
  }));

  const onSubmit = async (event: any) => {
    setSaving(true)
    try {
      const values = employeeForm.getValues()
      if (values.firstName.includes('=') || values.lastName.includes('=')) {
        enqueueSnackbar(t("common.Equal signs cannot appear in the name!"), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
        return
      }
      if (values.email && values?.email.length > 50) {
        enqueueSnackbar(t("common.The length of this field cannot exceed digits", {field_name: "Email", number: 50}), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
        return
      }
      const email_pattern = /^\w[-\w.+]*@([A-Za-z0-9][-A-Za-z0-9]+\.)+[A-Za-z]{2,14}$/;
      if (values?.email !== "" && !email_pattern.test(values?.email || '')) {
        enqueueSnackbar(t("common.Invalid email format"), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
        return
      }
      if (!values.email && values.verify) {
        enqueueSnackbar(t("common.Can not enable App Access without email."), {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
        return
      }
      values.departmentIdOrCode = values.dept.id
      // 统一使用 async/await 语法，让异步逻辑更清晰、更易维护
      try {
        const addRes = await EmployeeService.add(values);

        // 情况1：需要同步员工到设备
        if (addRes.data?.code === 'MUASEE023') {
          await new Promise((resolve: any) => {
            Dialog.confirm({
              title: t("common.Tips"),
              description: t("common.sync employee to device tips1"),
              content: t("common.sync employee to device tips2"),
              onConfirm: async () => {
                setOpen(false);
                const data = {
                  "employee_ids": [addRes.data?.data?.employee_id]
                };
                // 等待设备同步接口完成
                await DeviceService.addPersonByDeviceList(data);
                SnackbarUtil.success(t("common.Person added successfully."));
                resolve(); // 确认操作完成
              },
              onClose: () => {
                SnackbarUtil.success(t("common.Person added successfully."));
                setOpen(false);
                resolve(); // 关闭弹窗也视为完成
              }
            });
          });
        }
        // 情况2：接口正常返回（非400+状态）
        else if (addRes.status < 400) {
          enqueueSnackbar(t("common.Person added successfully."), {
            variant: "success",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
        }
        // 情况3：员工数量超限
        else if (addRes.data?.code === 'MUASEE041') {
          enqueueSnackbar(t("common.Employee limit reached for this subscription. Please upgrade to add more."), {
            variant: "error",
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
        }

        // 关键修改：所有异步操作完成后，再执行刷新和关闭
        if (props.reloadTable) {
          // 可选：如果后台异步任务耗时较长，可增加短暂延迟确保数据落地
          if (!values.verify) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
          props.reloadTable();
        }
        onClose();

      } catch (error) {
        // 增加异常捕获，避免接口报错导致流程卡死
        console.error("添加员工失败：", error);
        // enqueueSnackbar(t("common.Operation failed"), {
        //   variant: "error",
        //   anchorOrigin: {vertical: "top", horizontal: "center"}
        // });
        onClose();
      }
    } catch (e) {
      console.log("error===", e)
    } finally {
      setSaving(false)
      // if (props.reloadTable) {
      //   props.reloadTable()
      // }
      // onClose()
    }
  }

    return (
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
      >
        <Grid sx={{
          minWidth: '400px',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          position: 'relative',
        }}>
          <Grid sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '56px',
            padding: '0px 24px',
            borderBottom: '1px solid #F2F5F8',
          }}>
            <Grid>{title}</Grid>
            <Grid>
              <i style={{cursor: "pointer"}} className="icon iconfont icon-ic_close" onClick={onClose}></i>
            </Grid>
          </Grid>
          <EmployeeForm employeeForm={employeeForm} isDisable={false} paymentStatus={props.paymentStatus}/>
          <Grid sx={{borderTop: '1px solid #F2F5F8', position: 'absolute', bottom: '12px', left: '0', width: '100%'}}>
            <Grid style={{paddingTop: '12px', paddingLeft: '24px'}}>
              <Stack spacing={3} direction={"row"} justifyContent="start">
                <CancelButton onClick={onClose}/>
                <SavingButton children={t("common.confirm")} loading={saving}
                              onClick={employeeForm.handleSubmit(onSubmit)}/>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
      </Drawer>
    )
  }

  export default forwardRef(EmployeeNew)
